'use client';
import { config } from '@/apollo/config';
import { BANNER_LIST_QUERY } from '@/apollo/graphql/queries/bannerQueries/bannerQueries';
import { BannerEdge } from '@/apollo/graphql/types/bannerTypes';
import IconArrowWhiteLeft from '@/assets/icons/catalogs/icon-arrow-left-white.svg';
import IconArrowBlackRight from '@/assets/icons/catalogs/icon-arrow-right-white.svg';
import { Link } from '@/i18n/navigation';
import { useQuery } from '@apollo/client';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import Image from 'next/image';
import { SetStateAction, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import styles from './carousel.module.scss';
import './splide.css';

type Props = {};

interface ImageState {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
}

const CustomCarouselCategory = (props: Props) => {
  const splideRef = useRef<any>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageStates, setImageStates] = useState<{ [key: string]: ImageState }>(
    {}
  );
  const { data: bannerData, loading } = useQuery(BANNER_LIST_QUERY, {
    variables: { first: 100, filter: { bannerType: 'FULL' } },
  });

  useEffect(() => {
    // Add event listener to update the current slide index when slides change
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.on('moved', (newIndex: SetStateAction<number>) => {
        setCurrentSlide(newIndex);
      });
    }

    return () => {
      if (splide) {
        splide.off('moved');
      }
    };
  }, []);

  const goToNextSlide = () => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.go('>');
    }
  };
  const goToPrevSlide = () => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.go('<');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (!bannerData?.banners?.edges) return;

      const screenWidth = window.innerWidth;
      const newImageStates: { [key: string]: ImageState } = {};

      bannerData?.banners?.edges?.forEach((item: BannerEdge) => {
        let newImageUrl = `${config.publicApiUrl}${item?.node?.image}`;
        let newImageWidth = 1118;
        let newImageHeight = 216;

        if (screenWidth < 500 && item?.node?.imageSm) {
          newImageUrl = `${config.publicApiUrl}${item?.node?.imageSm}`;
          newImageWidth = 343;
          newImageHeight = 194;
        } else if (
          screenWidth >= 500 &&
          screenWidth < 1024 &&
          item?.node?.imageMd
        ) {
          newImageUrl = `${config.publicApiUrl}${item?.node?.imageMd}`;
          newImageWidth = 736;
          newImageHeight = 216;
        }

        newImageStates[item.node.id] = {
          imageUrl: newImageUrl,
          imageWidth: newImageWidth,
          imageHeight: newImageHeight,
        };
      });

      setImageStates(newImageStates);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [bannerData]);

  return (
    <div className={styles.carousel}>
      {loading ? (
        <div className={`${styles.skeleton}`}>
          <Skeleton />
        </div>
      ) : (
        <Splide
          ref={splideRef}
          options={{
            updateOnMove: true,
            type: 'loop',
            speed: 400,
            easing: 'linear',
            perPage: 1,
            perMove: 1,
            rewind: true,
            rewindSpeed: 1000,
            rewindByDrag: true,
            arrows: false,
            pagination: false,
            gap: '12px',
            breakpoints: {
              1200: {
                perPage: 1,
              },
              1024: {
                perPage: 1,
              },
              768: {
                perPage: 1,
                gap: '8px',
              },
              480: {
                perPage: 1,
                gap: '8px',
              },
              375: {
                perPage: 1,
                gap: '8px',
              },
            },
          }}
          hasTrack={false}
          style={{ padding: '0' }}
        >
          <SplideTrack>
            {bannerData?.banners?.edges.map((item: any) => (
              <SplideSlide key={item.node.id}>
                <div className={styles.slide}>
                  <Link href={item?.node?.url || '#'} passHref>
                    {imageStates[item.node.id] && (
                      <Image
                        className={styles.carouselImage}
                        src={imageStates[item.node.id]?.imageUrl}
                        alt={item?.node?.title}
                        layout="responsive"
                        width={imageStates[item.node.id]?.imageWidth}
                        height={imageStates[item.node.id]?.imageHeight}
                      />
                    )}
                  </Link>
                </div>
              </SplideSlide>
            ))}
          </SplideTrack>
          <div className={styles.controllers}>
            {loading ? (
              <div className={styles.customBtns}>
                <button
                  type="button"
                  className={styles.carouselNextButton}
                  onClick={goToPrevSlide}
                >
                  {' '}
                  <IconArrowWhiteLeft />
                </button>
                <button
                  type="button"
                  className={styles.carouselNextButton}
                  onClick={goToNextSlide}
                >
                  {' '}
                  <IconArrowBlackRight />
                </button>
              </div>
            ) : bannerData?.banners?.edges.length > 0 ? (
              <div className={styles.customBtns}>
                <button
                  type="button"
                  className={styles.carouselNextButton}
                  onClick={goToPrevSlide}
                >
                  {' '}
                  <IconArrowWhiteLeft />
                </button>
                <button
                  type="button"
                  className={styles.carouselNextButton}
                  onClick={goToNextSlide}
                >
                  {' '}
                  <IconArrowBlackRight />
                </button>
              </div>
            ) : null}

            <div className={styles.btnGroup}>
              <div className={styles.customPagination}>
                {bannerData?.banners?.edges.map((_: any, index: number) => (
                  <div
                    key={index}
                    className={`${styles.paginationDot} ${
                      currentSlide === index ? styles.active : ''
                    }`}
                    onClick={() => splideRef.current?.splide.go(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </Splide>
      )}
    </div>
  );
};

export default CustomCarouselCategory;
